/**
 * @file Purchase PPD Items Checkout Success component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import get from 'lodash.get';

import DiscoverAlbumFolderBrandedHeaderContainer from '../../containers/discover/DiscoverAlbumFolderBrandedHeaderContainer';
import { getAsset } from '../../util/assets';
import Spinner from '../global/Spinner';
import { PPD_CART_ITEM_TYPES } from '../../constants/ppd-purchase';
import {
  hasAlbumCustomPrintsInCart,
  hasDownloadInCart,
  hasPrintsInCart,
  hasSubscriptionInCart,
} from '../../util/ppd-purchase';
import AlbumFolderPurchaseSuccessMediaButton from './album-folder-purchase/AlbumFolderPurchaseSuccessMediaButton';
import { getPurchasedCustomPrintName } from '../../util/custom-prints';
import Button from '../global/Button';
import AppStoreLink from '../global/AppStoreLink';
import PlayStoreLink from '../global/PlayStoreLink';
import ContextualString from '../contextual/ContextualString';

class PurchasePpdCheckoutSuccess extends Component {
  static propTypes = {
    album: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    purchasedItems: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.keys(PPD_CART_ITEM_TYPES)),
        photo: PropTypes.shape({
          id: PropTypes.string,
        }),
        printSize: PropTypes.shape({
          id: PropTypes.string,
        }),
        printPackage: PropTypes.shape({
          id: PropTypes.string,
        }),
        quantity: PropTypes.number,
      }),
    ),
    pendingPurchasedCustomPrints: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    ),
  };

  static defaultProps = {
    pendingPurchasedCustomPrints: [],
  };

  render() {
    const { album, purchasedItems, pendingPurchasedCustomPrints } = this.props;

    if (!album) {
      return (
        <div className="wrapper-main content-wrap-gray d-flex">
          <div className="m-auto">
            <Spinner />
          </div>
        </div>
      );
    }

    const hasPurchasedDownload = hasDownloadInCart(purchasedItems);
    const hasPurchasedPrints = hasPrintsInCart(purchasedItems);
    const hasPurchasedIdentities = hasSubscriptionInCart(purchasedItems);
    const hasPurchasedAlbumCustomPrints =
      hasAlbumCustomPrintsInCart(purchasedItems);
    const pendingPurchasedCustomPrintsAmount =
      pendingPurchasedCustomPrints.length;
    const [firstPendingAlbumCustomPrint] = pendingPurchasedCustomPrints;
    const customPrintLabel = pluralize(
      getPurchasedCustomPrintName(firstPendingAlbumCustomPrint),
      pendingPurchasedCustomPrintsAmount,
    );

    const maybeRenderAppLinks = () => {
      if (hasPurchasedIdentities) {
        return (
          <div className="my-3">
            <ContextualString
              id="subscriptionSuccessAppDownload"
              type={get(album.album_type, 'album_type')}
              subtype={get(album.album_subtype, 'album_subtype')}
              defaultMessage="Download the FREE Waldo app so you can invite family and friends to the photo stream. When I find your photos, they’ll get app alerts to view them just like you!"
            />

            <div className="mt-3">
              <AppStoreLink className="mr-1" />
              <PlayStoreLink />
            </div>
          </div>
        );
      }

      if (hasPurchasedDownload) {
        return (
          <div className="my-3">
            Download the FREE Waldo app where you can share, post and order
            prints of your favorite pics!
            <div className="mt-3">
              <AppStoreLink className="mr-1" />
              <PlayStoreLink />
            </div>
          </div>
        );
      }

      return null;
    };

    return (
      <div className="wrapper-main content-wrap-gray">
        <div className="wrapper-main">
          <DiscoverAlbumFolderBrandedHeaderContainer
            title="Oh, Happy Day!"
            className="site-branded-header-limit-logo"
          />

          <div className="content-wrap-escape content-wrap-white p-3">
            <div className="text-center">
              <img
                src={getAsset('/waldo-singing.svg')}
                width="90"
                alt=""
                className="mb-1"
              />

              <h3 className="mb-1">Thanks for your purchase</h3>

              <p className="mb-4">
                {(hasPurchasedDownload || hasPurchasedIdentities) && (
                  <span id="purchased-downloads">
                    Your digital hi-res purchases can be viewed and downloaded{' '}
                    <a
                      href={`/view/album/${album.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      here
                    </a>
                    .{' '}
                  </span>
                )}

                {(hasPurchasedPrints || hasPurchasedAlbumCustomPrints) && (
                  <span id="purchased-prints">
                    Expect your prints to be delivered to you pronto!
                  </span>
                )}

                {pendingPurchasedCustomPrintsAmount > 0 && (
                  <Fragment>
                    <strong
                      id="purchased-album-custom-prints"
                      className="d-block my-2"
                    >
                      Press below to configure your purchased {customPrintLabel}
                      !
                    </strong>

                    <Button
                      to={`/purchase/album/${album.id}/custom-prints/${firstPendingAlbumCustomPrint.id}`}
                      btnSize="sm"
                      btnStyle="success-vibrant"
                    >
                      Configure my {customPrintLabel} &rarr;
                    </Button>
                  </Fragment>
                )}
              </p>

              {maybeRenderAppLinks()}

              {album.album_subtype === null && (
                <div className="purchase-success-follow-us">
                  <h3 className="mb-1">Are you a fan?</h3>
                  <p className="mt-1">Follow or Like Me Below</p>
                  <div className="purchase-blue-rect-separator" />
                  <div className="purchase-social-media-list px-3">
                    <AlbumFolderPurchaseSuccessMediaButton
                      title="waldophotos"
                      subtitle="Like me"
                      to="https://www.facebook.com/waldophotos"
                      icon={
                        <img
                          src={getAsset('/icon-facebook.png')}
                          width={35}
                          className="mr-2"
                          alt=""
                        />
                      }
                    />
                    <AlbumFolderPurchaseSuccessMediaButton
                      title="@waldophotos"
                      subtitle="Follow me"
                      to="https://www.instagram.com/waldophotos"
                      icon={
                        <img
                          src={getAsset('/icon-instagram.png')}
                          width={35}
                          className="mr-1"
                          alt=""
                        />
                      }
                    />
                    <AlbumFolderPurchaseSuccessMediaButton
                      title="@waldophotos"
                      subtitle="Follow me"
                      to="https://twitter.com/waldophotos"
                      icon={
                        <img
                          src={getAsset('/icon-twitter.png')}
                          width={35}
                          className="mr-1"
                          alt=""
                        />
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default PurchasePpdCheckoutSuccess;
